/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

/**
 * Normalizes a prefix by removing trailing slash.
 * @internal
 */
function normalizePrefix(prefix: string): string {
	return prefix.endsWith("/") ? prefix.slice(0, -1) : prefix;
}

/**
 * Ensures a path starts with a leading slash.
 * @internal
 */
function ensureLeadingSlash(path: string): string {
	return path.startsWith("/") ? path : `/${path}`;
}

/**
 * Transforms a URL path to a Hygraph slug by prepending the prefix.
 * Used when fetching content from Hygraph.
 *
 * @example
 * toHygraphSlug("/home", "/support") => "/support/home"
 * toHygraphSlug("/about", "/support") => "/support/about"
 * toHygraphSlug("/home", undefined) => "/home"
 *
 * @param urlPath - The URL path from the browser (e.g., "/home")
 * @param prefix - The pageSlugPrefix from WfRefApplicationOwner (e.g., "/support")
 * @returns The Hygraph slug with prefix prepended
 */
export function toHygraphSlug(urlPath: string, prefix?: string | null): string {
	if (!prefix) {
		return urlPath;
	}

	const normalizedPrefix = normalizePrefix(prefix);
	const normalizedPath = ensureLeadingSlash(urlPath);

	return `${normalizedPrefix}${normalizedPath}`;
}

/**
 * Transforms a Hygraph slug to a URL path by stripping the prefix.
 * Used when generating public URLs (e.g., for sitemaps, links).
 *
 * @example
 * toUrlPath("/support/home", "/support") => "/home"
 * toUrlPath("/support/about", "/support") => "/about"
 * toUrlPath("/home", undefined) => "/home"
 * toUrlPath("/other/page", "/support") => "/other/page" (no match, returns as-is)
 * toUrlPath("/supporting/page", "/support") => "/supporting/page" (no match, "supporting" != "support")
 *
 * @param hygraphSlug - The slug from Hygraph (e.g., "/support/home")
 * @param prefix - The pageSlugPrefix from WfRefApplicationOwner (e.g., "/support")
 * @returns The URL path with prefix stripped
 */
export function toUrlPath(hygraphSlug: string, prefix?: string | null): string {
	if (!prefix) {
		return hygraphSlug;
	}

	const normalizedPrefix = normalizePrefix(prefix);

	// Check if slug is exactly the prefix
	if (hygraphSlug === normalizedPrefix) {
		return "/";
	}

	// Check if slug starts with prefix followed by "/"
	const prefixWithSlash = `${normalizedPrefix}/`;
	if (hygraphSlug.startsWith(prefixWithSlash)) {
		const remainingPath = hygraphSlug.slice(normalizedPrefix.length);
		return ensureLeadingSlash(remainingPath);
	}

	// Slug doesn't match prefix pattern, return as-is
	return hygraphSlug;
}
